// Import de la configuration des polices
import './styles/config/_fonts';
/* import { fontPreloadLinks } from './styles/config/_fontPreLoad'; */
// Import des styles
import './App.css';
import './styles/main.scss';
// Import des composants
import { Routes, Route, useLocation } from 'react-router-dom';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { useEffect, useRef, useState } from 'react';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
// Import des composants créés
import Navbar from './layouts/Navbar/Navbar';
import ScrollToTop from './components/ScrollToTop/ScrollToTop';
import Footer from './layouts/Footer/Footer';
import HomePage from './pages/HomePage/HomePage';
import AboutPage from './pages/AboutPage/AboutPage';
import SupportPage from './pages/SupportPage/SupportPage';
import CirclesPage from './pages/CirclesPage/CirclesPage';
import WorkshopPage from './pages/WorkshopPage/WorkshopPage';
import ParenthoodPage from './pages/ParenthoodPage/ParenthoodPage';
import ToastNotification from './components/ToastNotification/ToastNotification';
import Error404Page from './pages/Error404Page/Error404Page';
import AstroParenthoodPage from './pages/AstroParenthoodPage/AstroParenthoodPage';
import WorkshopDetailsPage from './pages/WorkshopDetailsPage/WorkshopDetailsPage';

import useStrapiStatus from "./hooks/useStrapiStatus";
import Maintenance from "./components/Maintenance/Maintenance";
import Loading from "./components/Loading/Loading";

import EbookPage from './pages/EbookPage/EbookPage';
import NewsletterPage from './pages/NewsletterPage/NewsletterPage';
import UnsubscribeNewsletterPage from './pages/UnsubscribeNewsletterPage/UnsubscribeNewsletterPage';
import Legacy from './components/Legacy/Legacy';
import EbookModal from './pages/EbookPage/components/EbookModal/EbookModal';
import NewsletterModal from './pages/NewsletterPage/components/NewsletterModal/NewsletterModal';
import BlogPage from './pages/BlogPage/BlogPage';
import ArticleDetailPage from './pages/ArticleDetailPage/ArticleDetailPage';
import FreebiePage from './pages/FreebiePage/FreebiePage';
import FreebieModal from './pages/FreebiePage/components/FreebieModal/FreebieModal';
/** Strapi */
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import CacheStatus from './components/CacheStatus/CacheStatus';
import { preloadMainPages } from './services/cacheService';
import { queryClient, initializeQueryClient, USE_LOCAL_STORAGE } from './services/queryClientService';

/** Auth */
import { AuthProvider } from './contexts/AuthContext';
import LoginPage from './pages/auth/LoginPage/LoginPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage/ResetPasswordPage';
import CreatePasswordPage from './pages/auth/CreatePasswordPage/CreatePasswordPage';
/** Account */
import AccountPage from './pages/account/AccountPage/AccountPage';
import EditAccountPage from './pages/account/EditAccountPage/EditAccountPage';
import ChangePasswordPage from './pages/account/ChangePasswordPage/ChangePasswordPage';
import PurchasesPage from './pages/account/PurchasesPage/PurchasesPage';
import PaymentMethodPage from './pages/account/PaymentMethodPage/PaymentMethodPage';
/** Routes */
import PublicRoute from './components/routes/PublicRoute';
import PrivateRoute from './components/routes/PrivateRoute';
//** Trainings */
import TrainingPage from './pages/TrainingPage/TrainingPage';
import TrainingDetailsPage from './pages/TrainingDetailsPage/TrainingDetailsPage';
//** Payment */
import { PaymentProvider } from './contexts/PaymentContext';
import PaymentConfirmationPage from './pages/PaymentConfirmationPage/PaymentConfirmationPage';

import { baseStrapiURL } from './services/config';
import RedirectionPage from './pages/RedirectionPage/RedirectionPage';
import AccountActivationPage from './pages/auth/AccountActivationPage/AccountActivationPage';

// Initialiser le queryClient avec toutes les fonctionnalités (ATTENTION : hors de la function App)
initializeQueryClient();

function App() {
    const location = useLocation();
    const isAdminRoute = location.pathname.startsWith('/admin');
    const [notification, setNotification] = useState(null);
    const nodeRef = useRef(null);
    const [legacy, setLegacy] = useState(null);
    const [showLegacy, setShowLegacy] = useState(false);
    const [ebookModalContent, setEbookModalContent] = useState({
        Remerciements: '',
        Ebook: '',
    });
    const [showEbookModal, setShowEbookModal] = useState(false);
    const [newsletterModalContent, setNewsletterModalContent] = useState({
        Remerciements: '',
    });
    const [showNewsletterModal, setShowNewsletterModal] = useState(false);
    const [freebieModalContent, setFreebieModalContent] = useState({
        Remerciements: '',
    });
    const [showFreebieModal, setShowFreebieModal] = useState(false);
    
    const [isStrapiDown, setIsStrapiDown] = useState(false);
    const [isCheckingStrapi, setIsCheckingStrapi] = useState(true);
    
    // Référence pour contrôler si l'initialisation a déjà été faite
    const cacheInitializedRef = useRef(false);

    // Initialiser le préchargement des pages
    useEffect(() => {
        // Éviter les initialisations multiples
        if (!cacheInitializedRef.current) {
            console.debug("Initialisation du service de cache");

            // Preload après un court délai
            setTimeout(() => {
                preloadMainPages();
            }, 2000);

            cacheInitializedRef.current = true;
        }
    }, []);

    // Log l'état du stockage localStorage (peut être supprimé en production)
    useEffect(() => {
        console.debug(`Cache localStorage: désactivé`);
    }, []);

    useEffect(() => {
        const checkStrapi = async () => {
            try {
                const res = await fetch(`${process.env.REACT_APP_STRAPI_URL}/api/health`);
                if (!res.ok) throw new Error("Strapi error");
                setIsStrapiDown(false);
            } catch (err) {
                setIsStrapiDown(true);
            } finally {
                setIsCheckingStrapi(false);
            }
        };

        checkStrapi();

        const interval = setInterval(checkStrapi, 15000);
        return () => clearInterval(interval);
    }, []);
    console.log(process.env.REACT_APP_API_URL);
    console.log("DOWN ?", isStrapiDown);


    if (isCheckingStrapi) {
        return <Loading global={true} />; // ta page de loading globale
    }

    if (isStrapiDown) {
        return <Maintenance />;
    }

    return (
        <QueryClientProvider client={queryClient}>
            <AuthProvider>
                <PaymentProvider>
                    <div className="App">
                        <HelmetProvider>
                            {/* <Helmet>
                        {fontPreloadLinks.map((link, index) => (
                            <link key={index} {...link} />
                        ))}
                    </Helmet> */}
                            {!isAdminRoute && <Navbar />}
                            {notification && (
                                <ToastNotification
                                    {...notification}
                                    onClose={() => setNotification(null)}
                                />
                            )}
                            <TransitionGroup>
                                <CSSTransition
                                    key={location.key}
                                    classNames="fade"
                                    timeout={500}
                                    nodeRef={nodeRef}
                                >
                                    <div className="route-section">
                                        <Routes location={location}>
                                            <Route
                                                path="/"
                                                element={<HomePage />}
                                            />
                                            <Route
                                                path="/a-propos"
                                                element={<AboutPage />}
                                            />
                                            <Route
                                                path="/hey-mama-t-es-pas-seule"
                                                element={<RedirectionPage />}
                                            />
                                            <Route
                                                path="/astro-parentalite"
                                                element={<RedirectionPage />}
                                            />
                                            <Route
                                                path="/ebook-mama"
                                                element={<EbookPage
                                                    setEbookModalContent={setEbookModalContent}
                                                    setShowEbookModal={setShowEbookModal}
                                                />}
                                            />
                                            <Route
                                                path="/cercles-de-mamans"
                                                element={<WorkshopDetailsPage />}
                                            />
                                            <Route
                                                path="/accompagnement-individuel"
                                                element={<WorkshopDetailsPage />}
                                            />
                                            <Route
                                                path="/:workshopsListSlug"
                                                element={<WorkshopPage />}
                                            />
                                            <Route
                                                path="/ateliers/:workshopSlug"
                                                element={<WorkshopDetailsPage />}
                                            />
                                            <Route
                                                path="/blog"
                                                element={<BlogPage />}
                                            />
                                            <Route
                                                path="/blog/:slug"
                                                element={<ArticleDetailPage />}
                                            />
                                            <Route
                                                path="/newsletter"
                                                element={<NewsletterPage
                                                    setNotification={setNotification}
                                                    setNewsletterModalContent={setNewsletterModalContent}
                                                    setShowNewsletterModal={setShowNewsletterModal}
                                                />}
                                            />
                                            <Route
                                                path="/desinscription-newsletter/:token"
                                                element={<UnsubscribeNewsletterPage />}
                                            />
                                            <Route
                                                path="/freebies/:slug"
                                                element={<FreebiePage
                                                    setNotification={setNotification}
                                                    setFreebieModalContent={setFreebieModalContent}
                                                    setShowFreebieModal={setShowFreebieModal}
                                                />}
                                            />
                                            {/* Route pour la confirmation de paiement */}
                                            <Route
                                                path="/paiement/confirmation"
                                                element={
                                                    <PaymentConfirmationPage />
                                                }
                                            />
                                            <Route
                                                path="/activer-compte/:token"
                                                element={
                                                    <PublicRoute>
                                                        <AccountActivationPage />
                                                    </PublicRoute>
                                                }
                                            />
                                            <Route
                                                path="/connexion"
                                                element={
                                                    <PublicRoute>
                                                        <LoginPage />
                                                    </PublicRoute>
                                                }
                                            />
                                            <Route
                                                path="/reinitialiser-mot-de-passe/:token"
                                                element={
                                                    <PublicRoute>
                                                        <ResetPasswordPage />
                                                    </PublicRoute>
                                                }
                                            />
                                            <Route
                                                path="/creer-mot-de-passe/:token"
                                                element={
                                                    <PublicRoute>
                                                        <CreatePasswordPage />
                                                    </PublicRoute>
                                                }
                                            />
                                            <Route
                                                path="/mon-compte"
                                                element={
                                                    <PrivateRoute>
                                                        <AccountPage
                                                            setNotification={setNotification}
                                                        />
                                                    </PrivateRoute>
                                                }
                                            />
                                            <Route
                                                path="/mon-compte/modifier"
                                                element={
                                                    <PrivateRoute>
                                                        <EditAccountPage />
                                                    </PrivateRoute>
                                                }
                                            />
                                            <Route
                                                path="/mon-compte/changer-mot-de-passe"
                                                element={
                                                    <PrivateRoute>
                                                        <ChangePasswordPage />
                                                    </PrivateRoute>
                                                }
                                            />
                                            <Route
                                                path="/mon-compte/mes-achats"
                                                element={
                                                    <PrivateRoute>
                                                        <PurchasesPage />
                                                    </PrivateRoute>
                                                }
                                            />
                                            <Route
                                                path="/mon-compte/moyen-de-paiement"
                                                element={
                                                    <PrivateRoute>
                                                        <PaymentMethodPage />
                                                    </PrivateRoute>
                                                }
                                            />
                                            {/* <Route
                                                path="/mes-formations"
                                                element={
                                                    <PrivateRoute>
                                                        <TrainingPage />
                                                    </PrivateRoute>
                                                }
                                            /> */}
                                            <Route
                                                path="/mes-formations/:trainingSlug"
                                                element={
                                                    <PrivateRoute>
                                                        <TrainingDetailsPage />
                                                    </PrivateRoute>
                                                }
                                            />
                                            <Route
                                                path="*"
                                                element={<Error404Page />}
                                            />
                                        </Routes>
                                        {!isAdminRoute && <ScrollToTop />}
                                        {!isAdminRoute &&
                                            <Footer
                                                setNotification={setNotification}
                                                setLegacy={setLegacy}
                                                setShowLegacy={() => setShowLegacy(true)}
                                            />
                                        }
                                        {showLegacy && legacy && (
                                            <Legacy
                                                content={legacy}
                                                onClose={() => setShowLegacy(false)}
                                            />
                                        )}
                                        {showEbookModal && ebookModalContent && (
                                            <EbookModal
                                                content={ebookModalContent}
                                                downloadLink={ebookModalContent?.Ebook?.Fichier?.url ? baseStrapiURL + ebookModalContent.Ebook.Fichier.url : ''}
                                                onClose={() => setShowEbookModal(false)}
                                                setNotification={setNotification}
                                            />
                                        )}
                                        {showNewsletterModal && newsletterModalContent && (
                                            <NewsletterModal
                                                content={newsletterModalContent}
                                                onClose={() => setShowNewsletterModal(false)}
                                            />
                                        )}
                                        {showFreebieModal && freebieModalContent && (
                                            <FreebieModal
                                                content={freebieModalContent}
                                                onClose={() => setShowFreebieModal(false)}
                                            />
                                        )}
                                    </div>
                                </CSSTransition>
                            </TransitionGroup>
                        </HelmetProvider>
                    </div >
                </PaymentProvider>
            </AuthProvider>
            {process.env.NODE_ENV === 'development' && (
                <>
                    <div style={{ position: 'fixed', bottom: 16, left: 16, background: '#f0f0f0', padding: '4px 8px', borderRadius: 4, fontSize: 12, zIndex: 9999 }}>
                        localStorage: {USE_LOCAL_STORAGE ? 'ON' : 'OFF'}
                    </div>
                    <ReactQueryDevtools initialIsOpen={false} />
                </>
            )}
        </QueryClientProvider>
    );
}

export default App;